import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Tv, Flame, Hammer, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import SharePick from "@/components/SharePick";

const panel = "panel p-5";
const h2 = "font-heading font-black uppercase text-lg text-white mb-3 flex items-center gap-2";

function Team({ t, right, showScore }) {
  return (
    <div className={`flex items-center gap-4 flex-1 ${right ? "sm:flex-row-reverse sm:text-right" : ""}`}>
      {t.logo && <img src={t.logo} alt={t.abbr} className="w-16 h-16 sm:w-20 sm:h-20 object-contain" />}
      <div className="min-w-0">
        <div className={`font-heading font-black text-xl sm:text-2xl leading-tight ${t.winner ? "text-white" : "text-slate-300"}`}>{t.name}</div>
        {t.record && <div className="text-xs text-slate-500 font-mono-num" data-testid={`game-record-${t.abbr}`}>{t.record}</div>}
      </div>
      {showScore && <div className={`font-mono-num font-black text-4xl sm:text-5xl ml-auto ${right ? "sm:mr-auto sm:ml-0" : ""} ${t.winner ? "text-smasher-gold" : "text-slate-400"}`}>{t.score}</div>}
    </div>
  );
}

function Header({ g }) {
  const showScore = g.state !== "pre";
  return (
    <div className={`${panel} space-y-4`} data-testid="game-header">
      <div className="flex items-center justify-between text-xs">
        <span className="font-black text-smasher-gold tracking-widest">{g.league}</span>
        <span className={`font-bold px-2 py-0.5 rounded ${g.state === "in" ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-slate-300"}`} data-testid="game-status">{g.status}</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
        <Team t={g.away} showScore={showScore} />
        <span className="text-slate-600 font-black text-center">{g.league === "MLS" ? "VS" : "@"}</span>
        <Team t={g.home} right showScore={showScore} />
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400 border-t border-white/5 pt-3">
        <span>{new Date(g.date).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET</span>
        {g.venue && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{g.venue}{g.city ? `, ${g.city}` : ""}</span>}
        {g.broadcast && <span className="inline-flex items-center gap-1"><Tv className="w-3 h-3" />{g.broadcast}</span>}
        {g.spread && <span className="text-smasher-gold font-bold" data-testid="game-line">Line: {g.spread}{g.total ? ` · O/U ${g.total}` : ""}</span>}
      </div>
    </div>
  );
}

function Linescore({ g }) {
  const n = Math.max(g.home.line.length, g.away.line.length);
  if (!n) return null;
  const label = (i) => (g.league === "MLS" ? (i < 2 ? `${i + 1}H` : "ET") : g.league === "NHL" ? (i < 3 ? i + 1 : "OT") : i < 4 ? i + 1 : "OT");
  return (
    <div className={panel} data-testid="game-linescore">
      <table className="w-full text-sm font-mono-num">
        <thead><tr className="text-slate-500 text-xs"><td />{Array.from({ length: n }, (_, i) => <td key={i} className="text-center">{label(i)}</td>)}<td className="text-right">T</td></tr></thead>
        <tbody>
          {[g.away, g.home].map((t) => (
            <tr key={t.abbr} className={t.winner ? "text-white" : "text-slate-400"}>
              <td className="font-bold py-1">{t.abbr}</td>
              {t.line.map((v, i) => <td key={i} className="text-center">{v}</td>)}
              <td className="text-right font-black">{t.score}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Trends({ t }) {
  if (!t || (!t.projection && !t.items?.length)) return null;
  return (
    <div className="border-t border-white/5 pt-3 mt-2" data-testid="game-trends">
      <div className="text-xs font-black tracking-widest text-smasher-gold mb-1">SMASHER TRENDS</div>
      {t.projection && <div className="text-sm text-white font-bold mb-1">Projected score: <span className="font-mono-num">{t.projection}</span></div>}
      {t.items?.map((x, i) => <p key={i} className="text-xs text-slate-400 leading-relaxed">• {x}</p>)}
    </div>
  );
}

function PickBox({ g }) {
  if (!g.free_pick && !g.notes?.length && !g.trends) return null;
  const p = g.free_pick || g.notes?.[0];
  const matchup = `${g.away.abbr} ${g.league === "MLS" ? "vs" : "@"} ${g.home.abbr}`;
  return (
    <div className={`${panel} border-smasher-gold/40`} data-testid="game-pick">
      <h2 className={h2}><Flame className="w-5 h-5 text-smasher-gold" /> Smasher Pick</h2>
      {g.free_pick && (
        <div className="bg-smasher-gold/10 rounded-xl p-4 mb-3" data-testid="game-free-pick">
          <div className="flex justify-between font-heading font-black text-white text-lg"><span>{p.label}</span>{p.odds && <span className="text-emerald-400 font-mono-num">{p.odds}</span>}</div>
          <p className="text-sm text-slate-300 mt-1">{p.summary}</p>
          <div className="text-[10px] font-bold tracking-widest text-smasher-gold mt-2">TODAY'S FREE PICK</div>
        </div>
      )}
      {g.notes?.filter((n) => !g.free_pick || n.label !== g.free_pick.label).map((n, i) => (
        <div key={i} className="border-t border-white/5 py-2 text-sm">
          <div className="flex justify-between font-bold text-white"><span className="inline-flex items-center gap-1.5"><Hammer className="w-3.5 h-3.5 text-smasher-gold" />{n.label}</span>{n.odds && <span className="font-mono-num text-emerald-400">{n.odds}</span>}</div>
          <p className="text-slate-400 text-xs mt-1">{n.summary}</p>
        </div>
      ))}
      <Trends t={g.trends} />
      {p && <SharePick text={`${matchup} Smasher Pick: ${p.label}${p.odds ? ` (${p.odds})` : ""}`} />}
      <p className="text-[10px] text-slate-600 mt-3">Researched and written by the Sportsbook Smasher team.</p>
    </div>
  );
}

function Performers({ g }) {
  if (!g.leaders?.length && !g.goals?.length) return null;
  return (
    <div className={panel} data-testid="game-performers">
      <h2 className={h2}><Trophy className="w-5 h-5 text-smasher-gold" /> {g.goals?.length ? "Goals" : "Top Performers"}</h2>
      {g.goals?.length > 0 ? g.goals.map((x, i) => (
        <div key={i} className="text-sm py-1 border-b border-white/5 last:border-0"><span className="font-mono-num text-slate-500 w-10 inline-block">{x.min}</span> {x.team && <span className="font-bold text-white mr-1">{x.team}</span>}{x.text}</div>
      )) : (
        <div className="grid sm:grid-cols-3 gap-3">
          {g.leaders.map((l) => (
            <div key={l.cat} className="bg-black/40 rounded-xl p-3 flex items-center gap-3">
              {l.headshot ? <img src={l.headshot} alt="" className="w-12 h-12 rounded-full object-cover bg-white/10" /> : <span className="w-12 h-12 rounded-full bg-white/10" />}
              <div className="min-w-0"><div className="text-[10px] text-smasher-gold font-bold uppercase">{l.cat}</div><div className="text-white font-bold truncate">{l.player} <span className="text-slate-500 text-xs">{l.team}</span></div><div className="text-xs text-slate-300">{l.value}</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TeamStats({ g }) {
  if (!g.team_stats?.length) return null;
  return (
    <div className={`${panel} min-w-0`} data-testid="game-team-stats">
      <h2 className={h2}>{g.state === "pre" ? "Season Stats" : "Team Stats"}</h2>
      <div className="grid grid-cols-[1fr_auto_1fr] text-sm">
        <div className="font-bold text-white pb-2">{g.away.abbr}</div><div /><div className="font-bold text-white text-right pb-2">{g.home.abbr}</div>
        {g.team_stats.map((s) => [
          <div key={`a${s.label}`} className="font-mono-num text-slate-200 py-1 border-t border-white/5">{s.away ?? "-"}</div>,
          <div key={`l${s.label}`} className="text-[11px] text-slate-500 py-1 px-3 text-center border-t border-white/5">{s.label}</div>,
          <div key={`h${s.label}`} className="font-mono-num text-slate-200 py-1 text-right border-t border-white/5">{s.home ?? "-"}</div>,
        ])}
      </div>
    </div>
  );
}

function BoxScore({ g }) {
  const [ti, setTi] = useState(0);
  if (!g.players?.length) return null;
  const team = g.players[ti] || g.players[0];
  return (
    <div className={`${panel} min-w-0`} data-testid="game-boxscore">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className={`${h2} mb-0`}>Box Score</h2>
        <div className="flex gap-1">
          {g.players.map((p, i) => (
            <button key={p.team} onClick={() => setTi(i)} className={`px-3 py-1 rounded-md text-xs font-black ${ti === i ? "btn-gold" : "bg-white/5 text-slate-400"}`} data-testid={`boxscore-tab-${p.team}`}>{p.team}</button>
          ))}
        </div>
      </div>
      {team.groups.map((grp, gi) => (
        <div key={gi} className="mb-4 overflow-x-auto">
          <div className="text-xs font-black text-smasher-gold tracking-widest mb-1">{(grp.name || "PLAYERS").toUpperCase()}</div>
          <table className="w-full text-xs font-mono-num whitespace-nowrap">
            <thead><tr className="text-slate-500"><td className="py-1 pr-3 sticky left-0 bg-[#0b0b0b]">PLAYER</td>{grp.labels.map((l) => <td key={l} className="px-2 text-right">{l}</td>)}</tr></thead>
            <tbody>
              {grp.rows.map((r, ri) => (
                <tr key={ri} className="border-t border-white/5">
                  <td className={`py-1 pr-3 sticky left-0 bg-[#0b0b0b] ${r.starter ? "text-white font-bold" : "text-slate-300"}`}>{r.name}</td>
                  {r.stats.map((v, i) => <td key={i} className="px-2 text-right text-slate-300">{v}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function LastFive({ g }) {
  if (!g.last5?.length || g.state !== "pre") return null;
  return (
    <div className={panel} data-testid="game-last5">
      <h2 className={h2}>Last 5 Games</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {g.last5.map((t) => (
          <div key={t.team}>
            <div className="font-bold text-white mb-1">{t.team}</div>
            {t.games.map((x, i) => (
              <div key={i} className="flex gap-3 text-xs py-1 border-t border-white/5">
                <span className={`w-4 font-black ${x.result === "W" ? "text-emerald-400" : x.result === "L" ? "text-red-400" : "text-slate-400"}`}>{x.result}</span>
                <span className="text-slate-400 w-14">{x.at} {x.opp}</span><span className="font-mono-num text-slate-300">{x.score}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function GamePage() {
  const { league, id } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/game/${league}/${id}`).then((r) => setG(r.data)).catch(() => setErr(true));
  }, [league, id]);
  const live = g?.state === "in";
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => api.get(`/game/${league}/${id}`).then((r) => setG(r.data)).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, [live, league, id]);
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="game-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-smasher-gold" data-testid="game-back"><ArrowLeft className="w-4 h-4" /> Back</Link>
      {err && <div className="panel p-10 text-center text-slate-500" data-testid="game-error">We couldn't load this game.</div>}
      {!g && !err && <div className="panel p-10 text-center text-slate-500">Loading game…</div>}
      {g && (
        <>
          {live && <div className="flex w-full text-xs text-emerald-400 font-bold items-center gap-2" data-testid="game-live-refresh"><span className="w-2 h-2 rounded-full bg-emerald-400 live-dot" /> LIVE · score and stats update every minute</div>}
          <Header g={g} />
          <PickBox g={g} />
          <Linescore g={g} />
          <Performers g={g} />
          <LastFive g={g} />
          <div className={`grid grid-cols-1 gap-5 items-start ${g.players?.length ? "lg:grid-cols-[1fr_2fr]" : ""}`}>
            <TeamStats g={g} />
            <BoxScore g={g} />
          </div>
        </>
      )}
    </div>
  );
}
